import React, { FC } from 'react';
import {
  MessageInputContextProvider,
  TriggerSettings,
  useMessageInputContext,
  UserTriggerSetting,
} from 'stream-chat-react';
import { UserResponse } from 'stream-chat';
import { useDebouncedCallback } from 'use-debounce';

import { callUserSuggestQuery } from 'app-react-query/imperative';
import { ProfileSuggestionFragment } from 'api';
import { useNotify } from 'hooks';

import { MessageInputContextValue } from 'stream-chat-react/dist/context/MessageInputContext';
import { useEmojiTrigger } from 'stream-chat-react/dist/components/MessageInput/hooks/useEmojiTrigger';
import { useCommandTrigger } from 'stream-chat-react/dist/components/MessageInput/hooks/useCommandTrigger';
import { StreamChatGenerics } from '../types';
import Suggestion from './Suggestion';

type UserResponseArray = Array<UserResponse>;
type OnUsersReady = (users: UserResponseArray) => void;

const TriggerProvider: FC = ({ children }) => {
  const currentContextValue = useMessageInputContext<StreamChatGenerics>();
  const { onSelectUser } = useMessageInputContext();
  const notify = useNotify();

  const [debounced] = useDebouncedCallback(userSuggestQuery, 100);

  async function userSuggestQuery(prefix: string, onReady: OnUsersReady) {
    let queryResult = [] as UserResponseArray;

    try {
      const suggestQuery = await callUserSuggestQuery({ prefix });
      queryResult = suggestQuery.suggest.results.map((user: ProfileSuggestionFragment) => ({
        name: user.title,
        ...user,
      }));
    } catch {
      notify.queryError();
    }

    return onReady(queryResult);
  }

  const userTrigger: UserTriggerSetting<StreamChatGenerics> = {
    dataProvider: (
      query: string,
      text: any,
      onReady: (data: UserResponseArray, token: string) => void
    ) => {
      debounced(query, data => {
        onReady(data, query);
      });
    },
    component: Suggestion,
    output: entity => ({
      text: `@${entity.name || entity.id}`,
      caretPosition: 'next',
    }),
    callback: (item: UserResponse) => {
      onSelectUser(item);
    },
  };

  const customTriggers: TriggerSettings<StreamChatGenerics> = {
    '@': userTrigger,
    ':': useEmojiTrigger(currentContextValue.emojiIndex),
    '/': useCommandTrigger<StreamChatGenerics>(),
  };

  const updatedContextValue: MessageInputContextValue<StreamChatGenerics> = {
    ...currentContextValue,
    autocompleteTriggers: customTriggers,
  };

  return (
    <MessageInputContextProvider value={updatedContextValue}>
      {children}
    </MessageInputContextProvider>
  );
};

export default TriggerProvider;
